import React, {useEffect, useState} from "react";
import {
    Box,
    Typography,
    Button,
    TextField,
    Divider,
    Link,
    InputAdornment,
    Autocomplete,
} from "@mui/material";
import {LoadingButton} from "@mui/lab";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlined from "@mui/icons-material/VisibilityOffOutlined";
import {Formik, Form, Field, ErrorMessage} from "formik";
import * as Yup from "yup";
import logo from "../../../../assets/react-logo-no-background.svg";
import {KEY} from "../../../../Constants";
import CryptoJS from "crypto-js";
import {useNavigate} from "react-router-dom";
import {showSnackbar} from "../../../SnackBar/SnackBar";
import TOTPAuthentication from "../../../TOTPAuth/TOTPAuthentication";
import {authApi, dashboardApi, userManagementApi, legalComplianceApi} from "../../../../services/api";
import LegalComplianceModal from "../../../LegalCompliance/LegalComplianceModal";
import { handle401Unauthorized, handle403Forbidden } from '../../../../utils/authErrorHandler';


const LoginRight = (props) => {
    let userData = 0;

    const [type, setType] = useState("password");
    const [icon, setIcon] = useState(<VisibilityOutlined/>);
    const [loginType, setLoginType] = useState("landlord"); // 'landlord' or 'subuser'

    const initialValues = {
        email_address: "",
        password: "",
    };

    const validationSchema = Yup.object().shape({
        email_address: Yup.string()
            .email('Invalid email address')
            .required('Email is required'),
        password: Yup.string().required('Password is required'),
    });

    const navigate = useNavigate();
    const [userTypes, setUserTypes] = React.useState([]);
    const [selectedUserType, setSelectedUserType] = React.useState();

    const [loading, setLoading] = React.useState(false);
    const [proceed, setProceed] = React.useState(false);
    const [authenticationPage, setAuthenticationPage] = useState();

    // TOTP specific state
    const [, setSessionToken] = useState("");
    const [, setLoginQRCode] = useState("");

    // Legal compliance state
    const [showLegalCompliance, setShowLegalCompliance] = useState(false);
    const [legalComplianceData, setLegalComplianceData] = useState({
        pendingDocuments: [],
        hasSignature: false,
        userData: null
    });

    useEffect(() => {
        const getUserTypes = async () => {
            try {
                const response = await authApi.getUserTypes();

                if (response.ok) {
                    setUserTypes(response.data);

                    // Automatically select LANDLORD as the default user type
                    const landlordType = response.data.find(
                        type => type.description === "LANDLORD" || type.description.toUpperCase() === "LANDLORD"
                    );

                    if (landlordType) {
                        setSelectedUserType(landlordType);
                    }
                } else {
                    showSnackbar({
                        title: "Error!",
                        message: "Failed Fetching System User Types",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
            } catch (err) {
                showSnackbar({
                    title: "Error!",
                    message: "Something went wrong. Kindly try again later.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            }
        };

        getUserTypes();
    }, []);

    const onSubmit = async (values) => {
        setLoading(true);

        // Handle sub-user login
        if (loginType === "subuser") {
            try {
                const response = await userManagementApi.subUserLogin({
                    email_address: values.email_address,
                    password: values.password
                });

                if (response.ok) {
                    const data = response.data;
                    userData = data;

                    // Sub-users may also have 2FA enabled
                    let two_fa = new Map(Object.entries(data)).get("two_factor_enabled");

                    if (two_fa === "YES") {
                        authenticateWithTOTP(values.email_address, values.password);
                    } else {
                        // No 2FA, proceed with sub-user login
                        getUserDetails(data);
                    }
                } else if (response.status === 400) {
                    showSnackbar({
                        title: "Error!",
                        message: "Invalid Credentials Provided.",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                    setLoading(false);
                } else if (response.status === 401) {
                    showSnackbar({
                        title: "Warning!",
                        message: "Maximum concurrent logins reached",
                        type: "warning",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                    setLoading(false);
                } else {
                    showSnackbar({
                        title: "Error!",
                        message: "Something went wrong. Kindly try again later.",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                    setLoading(false);
                }
            } catch (err) {
                setLoading(false);
                console.error(err);
                showSnackbar({
                    title: "Error!",
                    message: "Something went wrong. Kindly try again later.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            }
            return;
        }

        // Handle landlord login
        if (selectedUserType === undefined || selectedUserType === null) {
            showSnackbar({
                title: "Error!",
                message: "User Type Not Selected",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            setLoading(false);
            return;
        }

        values = {...values, user_type: selectedUserType.id};

        try {
            // Step 1: Login to PMS system first
            const response = await authApi.login(values);
            const data = response.data;
            userData = data;

            if (response.ok) {
                let two_fa = new Map(Object.entries(data)).get("two_factor_enabled");

                if (two_fa === "YES") {
                    // Step 2: User has 2FA enabled, authenticate with TOTP system
                    authenticateWithTOTP(values.email_address, values.password);
                } else {
                    // No 2FA, proceed with normal login
                    getUserDetails(data);
                }
            } else if (response.status === 400) {
                showSnackbar({
                    title: "Error!",
                    message: "Invalid Credentials Provided.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
                setLoading(false);
            } else if (response.status === 401) {
                showSnackbar({
                    title: "Warning!",
                    message: "Maximum concurrent logins reached",
                    type: "warning",
                    position: {vertical: "bottom", horizontal: "right"},
                });
                setLoading(false);
            } else {
                showSnackbar({
                    title: "Error!",
                    message: "Something went wrong. Kindly try again later.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
                setLoading(false);
            }
        } catch (err) {
            setLoading(false);
            console.error(err);
            showSnackbar({
                title: "Error!",
                message: "Something went wrong. Kindly try again later.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
        }
    };

    // Step 3: Authenticate with TOTP system
    const authenticateWithTOTP = (email, password) => {
        fetch("http://localhost:8080/api/auth/login", {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                username: email,
                password: password
            }),
        })
            .then(async (response) => {
                 // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
            if (response.status === 200) {
                    let totpData = await response.json();

                    if (totpData.requiresTwoFactor) {
                        // Step 4: Show 2FA verification
                        setSessionToken(totpData.sessionToken);
                        show2FAVerification(totpData.sessionToken);
                    } else {
                        // This shouldn't happen if 2FA is enabled, but handle it
                        getUserDetails(userData);
                    }
                } else {
                    let errorData = await response.json();
                    showSnackbar({
                        title: "Error!",
                        message: errorData.message || "Authentication system error. Please contact support.",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                    setLoading(false);
                }
            })
            .catch((err) => {
                console.log("TOTP login error:", err);
                showSnackbar({
                    title: "Error!",
                    message: "Authentication system unavailable. Please try again later.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
                setLoading(false);
            });
    };

    // Step 5: Show 2FA verification page
    const show2FAVerification = (sessionToken) => {
        // Generate QR code for mobile scanning
        fetch(`http://localhost:8080/api/auth/2fa/login-qr?sessionToken=${sessionToken}`, {
            method: "GET"
        })
            .then(async (response) => {
                 // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
            if (response.status === 200) {
                    let qrData = await response.json();
                    setLoginQRCode(qrData.qrCodeImage);

                    // Show 2FA authentication page
                    setAuthenticationPage(
                        <TOTPAuthentication
                            sessionToken={sessionToken}
                            qrCodeImage={qrData.qrCodeImage}
                            onCodeVerified={complete2FALogin}
                            onError={(message) => {
                                showSnackbar({
                                    title: "Error!",
                                    message: message,
                                    type: "error",
                                    position: {vertical: "bottom", horizontal: "right"},
                                });
                                setLoading(false);
                                setProceed(false);
                            }}
                            onBack={() => {
                                setProceed(false);
                                setLoading(false);
                                setSessionToken("");
                                setLoginQRCode("");
                            }}
                        />
                    );
                    setProceed(true);
                } else {
                    let errorData = await response.json();
                    showSnackbar({
                        title: "Error!",
                        message: errorData.message || "Error generating authentication QR code.",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                    setLoading(false);
                }
            })
            .catch((err) => {
                console.log("QR generation error:", err);
                showSnackbar({
                    title: "Error!",
                    message: "Error setting up authentication. Please try again.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
                setLoading(false);
            });
    };

    // Step 6: Complete 2FA login
    const complete2FALogin = (sessionToken, totpCode) => {
        fetch("http://localhost:8080/api/auth/2fa/verify-login", {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                sessionToken: sessionToken,
                code: totpCode
            }),
        })
            .then(async (response) => {
                let verificationResult = await response.json();

                if (response.status === 200 && verificationResult.success) {
                    // 2FA verification successful, complete login
                    showSnackbar({
                        title: "Success!",
                        message: "2FA verification successful!",
                        type: "success",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                    getUserDetails(userData);
                } else {
                    showSnackbar({
                        title: "Error!",
                        message: verificationResult.message || "Invalid authentication code. Please try again.",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
            })
            .catch((err) => {
                console.log("2FA verification error:", err);
                showSnackbar({
                    title: "Error!",
                    message: "Authentication error. Please try again.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            });
    };

    const handleLegalComplianceAccepted = async () => {
        // Close modal
        setShowLegalCompliance(false);

        // Continue with normal login flow using stored user details
        const userDetails = legalComplianceData.userData.fullUserDetails;

        try {
            setLoading(true);
            const response = await dashboardApi.getDetails(userDetails);

            if (response.ok) {
                setLoading(false);
                const data = response.data;

                const map1 = new Map(Object.entries(data));
                const details = new Map(Object.entries(userDetails));
                const subscription = new Map();

                subscription.set("subscription_expired", details.get("subscription_expired"));
                subscription.set("subscription_amount", details.get("subscription_amount"));

                let myMap = CryptoJS.AES.encrypt(
                    JSON.stringify(Array.from(details.entries())),
                    KEY
                ).toString();
                let portfolioDetails = CryptoJS.AES.encrypt(
                    JSON.stringify(Array.from(map1.entries())),
                    KEY
                ).toString();
                let subscriptionDetails = CryptoJS.AES.encrypt(
                    JSON.stringify(Array.from(subscription.entries())),
                    KEY
                ).toString();

                localStorage.setItem("myMap", myMap);
                localStorage.setItem("isNew", userDetails.new);
                localStorage.portfolioDetails = portfolioDetails;
                localStorage.subscriptionDetails = subscriptionDetails;

                navigate("/home", {
                    state: {
                        userDetails: userDetails,
                    },
                });

                showSnackbar({
                    title: "Success!",
                    message: "Login Successful",
                    type: "success",
                    position: {vertical: "bottom", horizontal: "right"},
                });

                props.refresh();
                props.fn();
            } else {
                setLoading(false);
                // For 403 errors, the client.js already displayed the detailed error message
                // No need to show another generic error
                if (response.status !== 403) {
                    showSnackbar({
                        title: "Error!",
                        message: "Failed fetching details",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
            }
        } catch (err) {
            setLoading(false);
            console.error(err);
            showSnackbar({
                title: "Error!",
                message: "Something went wrong. Kindly try again later.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
        }
    };

    const getUserDetails = async (userDetails) => {
        try {
            // Check for legal compliance requirements
            if (userDetails.legal_compliance?.requires_acceptance) {
                const legalData = userDetails.legal_compliance;

                // Determine user_id and user_type based on login type
                let userId, userType;

                if (loginType === "subuser") {
                    userId = userDetails.sub_user_id;
                    userType = "SUB_USER";
                } else {
                    userId = userDetails.landlord_id;
                    userType = "LANDLORD";
                }

                // **IMPORTANT**: Store user data in localStorage BEFORE showing modal
                // This ensures the access_token is available for API calls from the modal
                const details = new Map(Object.entries(userDetails));
                let myMap = CryptoJS.AES.encrypt(
                    JSON.stringify(Array.from(details.entries())),
                    KEY
                ).toString();
                localStorage.setItem("myMap", myMap);
                localStorage.setItem("isNew", userDetails.new);

                // Show legal compliance modal
                setLegalComplianceData({
                    pendingDocuments: legalData.pending_documents || [],
                    hasSignature: legalData.has_signature || false,
                    userData: {
                        user_id: userId,
                        user_type: userType,
                        fullUserDetails: userDetails // Store full details for later
                    }
                });
                setShowLegalCompliance(true);
                setLoading(false);
                return; // Stop here, let user accept documents
            }

            // No legal compliance needed, proceed normally
            const response = await dashboardApi.getDetails(userDetails);

            if (response.ok) {
                setLoading(false);
                const data = response.data;

                const map1 = new Map(Object.entries(data));
                const details = new Map(Object.entries(userDetails));
                const subscription = new Map();

                subscription.set("subscription_expired", details.get("subscription_expired"));
                subscription.set("subscription_amount", details.get("subscription_amount"));

                let myMap = CryptoJS.AES.encrypt(
                    JSON.stringify(Array.from(details.entries())),
                    KEY
                ).toString();
                let portfolioDetails = CryptoJS.AES.encrypt(
                    JSON.stringify(Array.from(map1.entries())),
                    KEY
                ).toString();
                let subscriptionDetails = CryptoJS.AES.encrypt(
                    JSON.stringify(Array.from(subscription.entries())),
                    KEY
                ).toString();

                localStorage.setItem("myMap", myMap);
                localStorage.setItem("isNew", userDetails.new);
                localStorage.portfolioDetails = portfolioDetails;
                localStorage.subscriptionDetails = subscriptionDetails;

                navigate("/home", {
                    state: {
                        userDetails: userDetails,
                    },
                });

                showSnackbar({
                    title: "Success!",
                    message: "Login Successful",
                    type: "success",
                    position: {vertical: "bottom", horizontal: "right"},
                });

                props.refresh();
                props.fn();
            } else {
                setLoading(false);
                // For 403 errors, the client.js already displayed the detailed error message
                // No need to show another generic error
                if (response.status !== 403) {
                    showSnackbar({
                        title: "Error!",
                        message: "Failed fetching details",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
            }
        } catch (err) {
            setLoading(false);
            console.error(err);
            showSnackbar({
                title: "Error!",
                message: "Something went wrong. Kindly try again later.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
        }
    };

    return (
        <Box
            sx={{
                flex: 1,
                padding: 4,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            {proceed ? (
                <>{authenticationPage}</>
            ) : (
                <>
                    <img
                        style={{
                            width: "250px",
                            height: "150px",
                        }}
                        src={logo}
                        alt="React Logo"
                    />

                    <Typography variant="h4" sx={{mb: 2}}>
                        Welcome back
                    </Typography>

                    {/* Login Type Toggle */}
                    <Box sx={{width: "100%", maxWidth: "400px", mb: 2}}>
                        <Box
                            sx={{
                                display: "flex",
                                border: "1px solid #E0E0E0",
                                borderRadius: "8px",
                                overflow: "hidden",
                            }}
                        >
                            <Button
                                fullWidth
                                variant={loginType === "landlord" ? "contained" : "outlined"}
                                onClick={() => setLoginType("landlord")}
                                sx={{
                                    borderRadius: 0,
                                    backgroundColor: loginType === "landlord" ? "#2C5283" : "transparent",
                                    color: loginType === "landlord" ? "#FFF" : "#2C5283",
                                    border: "none",
                                    "&:hover": {
                                        backgroundColor: loginType === "landlord" ? "#1e3a5f" : "#f5f5f5",
                                        border: "none",
                                    },
                                }}
                            >
                                Landlord Login
                            </Button>
                            <Button
                                fullWidth
                                variant={loginType === "subuser" ? "contained" : "outlined"}
                                onClick={() => setLoginType("subuser")}
                                sx={{
                                    borderRadius: 0,
                                    backgroundColor: loginType === "subuser" ? "#2C5283" : "transparent",
                                    color: loginType === "subuser" ? "#FFF" : "#2C5283",
                                    border: "none",
                                    "&:hover": {
                                        backgroundColor: loginType === "subuser" ? "#1e3a5f" : "#f5f5f5",
                                        border: "none",
                                    },
                                }}
                            >
                                Sub-User Login
                            </Button>
                        </Box>
                    </Box>

                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={onSubmit}
                    >
                        {({errors, touched}) => (
                            <Form style={{width: "100%", maxWidth: "400px"}}>
                                <Field
                                    as={TextField}
                                    name="email_address"
                                    label="Email"
                                    fullWidth
                                    margin="normal"
                                    InputLabelProps={{shrink: true}}
                                    error={touched.email_address && !!errors.email_address}
                                    helperText={<ErrorMessage name="email_address"/>}
                                />

                                <Field
                                    as={TextField}
                                    name="password"
                                    label="Password"
                                    type={type}
                                    fullWidth
                                    margin="normal"
                                    InputLabelProps={{shrink: true}}
                                    error={touched.password && !!errors.password}
                                    helperText={<ErrorMessage name="password"/>}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Button
                                                    onClick={() => {
                                                        setType(type === "password" ? "text" : "password");
                                                        setIcon(type === "password" ? <VisibilityOffOutlined/> :
                                                            <VisibilityOutlined/>);
                                                    }}
                                                >
                                                    {icon}
                                                </Button>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                {/* Account Type dropdown is hidden - LANDLORD is automatically selected in the background */}

                                <LoadingButton
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    sx={{mt: 2, mb: 2, backgroundColor: "#3799D6", color: "#FFF"}}
                                    loading={loading}
                                >
                                    Sign In
                                </LoadingButton>

                                <Divider sx={{my: 2}}>or</Divider>

                                <Typography align="center">
                                    Don't have an account?{" "}
                                    <Link href="/signup" underline="hover" color="primary">
                                        Sign up
                                    </Link>
                                </Typography>
                            </Form>
                        )}
                    </Formik>
                </>
            )}

            {/* Legal Compliance Modal */}
            <LegalComplianceModal
                open={showLegalCompliance}
                pendingDocuments={legalComplianceData.pendingDocuments}
                hasExistingSignature={legalComplianceData.hasSignature}
                userData={legalComplianceData.userData}
                onAccepted={handleLegalComplianceAccepted}
                onClose={() => {}} // Non-dismissible, handled by component
            />
        </Box>
    );
}

export default LoginRight;